import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAdmin } from "@/integrations/supabase/admin";

export interface ProductSize {
  id: string;
  product_id: string;
  size_name: string;
  size_display_name: string;
  price: number;
  original_price: number | null;
  is_available: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProductSizeData {
  product_id: string;
  size_name: string;
  size_display_name: string;
  price: number;
  original_price?: number;
  is_available?: boolean;
  sort_order?: number;
}

export interface UpdateProductSizeData extends Partial<CreateProductSizeData> {
  id: string;
}

export function useProductSizes(productId?: string) {
  const [sizes, setSizes] = useState<ProductSize[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSizes = async (id?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from("product_sizes")
        .select("*")
        .order("sort_order", { ascending: true });

      if (id) {
        query = query.eq("product_id", id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSizes(data || []);
    } catch (err) {
      console.error("Error fetching product sizes:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch product sizes");
    } finally {
      setLoading(false);
    }
  };

  const createSize = async (sizeData: CreateProductSizeData): Promise<ProductSize | null> => {
    try {
      setError(null);
      
      const insertData = {
        ...sizeData,
        is_available: sizeData.is_available ?? true,
        sort_order: sizeData.sort_order ?? 0,
      };
      
      // Use admin client to bypass RLS policies
      const { data, error } = await supabaseAdmin
        .from("product_sizes")
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setSizes(prev => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
      return data;
    } catch (err) {
      console.error("Error creating product size:", err);
      setError(err instanceof Error ? err.message : "Failed to create product size");
      return null;
    }
  };

  const updateSize = async (sizeData: UpdateProductSizeData): Promise<ProductSize | null> => {
    try {
      setError(null);
      
      const { id, ...updateData } = sizeData;
      
      // Use admin client to bypass RLS policies
      const { data, error } = await supabaseAdmin
        .from("product_sizes")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setSizes(prev => prev.map(s => s.id === id ? data : s).sort((a, b) => a.sort_order - b.sort_order));
      return data;
    } catch (err) {
      console.error("Error updating product size:", err);
      setError(err instanceof Error ? err.message : "Failed to update product size");
      return null;
    }
  };

  const deleteSize = async (sizeId: string): Promise<boolean> => {
    try {
      setError(null);
      
      // Use admin client to bypass RLS policies
      const { error } = await supabaseAdmin
        .from("product_sizes")
        .delete()
        .eq("id", sizeId);

      if (error) throw error;
      
      // Update local state
      setSizes(prev => prev.filter(s => s.id !== sizeId));
      return true;
    } catch (err) {
      console.error("Error deleting product size:", err);
      setError(err instanceof Error ? err.message : "Failed to delete product size");
      return false;
    }
  };

  const deleteSizesByProduct = async (productId: string): Promise<boolean> => {
    try {
      setError(null);
      
      // Use admin client to bypass RLS policies
      const { error } = await supabaseAdmin
        .from("product_sizes")
        .delete()
        .eq("product_id", productId);

      if (error) throw error;
      
      // Update local state
      setSizes(prev => prev.filter(s => s.product_id !== productId));
      return true;
    } catch (err) {
      console.error("Error deleting product sizes:", err);
      setError(err instanceof Error ? err.message : "Failed to delete product sizes");
      return false;
    }
  };

  const reorderSizes = async (sizeIds: string[]): Promise<boolean> => {
    try {
      setError(null);
      
      const updates = sizeIds.map((id, index) => ({
        id,
        sort_order: index + 1
      }));

      // Use admin client to bypass RLS policies
      const { error } = await supabaseAdmin
        .from("product_sizes")
        .upsert(updates);

      if (error) throw error;
      
      // Update local state
      setSizes(prev => 
        prev.map(size => {
          const update = updates.find(u => u.id === size.id);
          return update ? { ...size, sort_order: update.sort_order } : size;
        }).sort((a, b) => a.sort_order - b.sort_order)
      );
      
      return true;
    } catch (err) {
      console.error("Error reordering product sizes:", err);
      setError(err instanceof Error ? err.message : "Failed to reorder product sizes");
      return false;
    }
  };

  useEffect(() => {
    fetchSizes(productId);
  }, [productId]);

  return {
    sizes,
    loading,
    error,
    fetchSizes,
    createSize,
    updateSize,
    deleteSize,
    deleteSizesByProduct,
    reorderSizes,
  };
}
